import React, { useState, useRef, useCallback } from 'react';
import { LoadScript, GoogleMap, Marker, Autocomplete } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import './AddressInput.css';

const libraries = ['places'];

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const DEFAULT_CENTER = { lat: -34.6037, lng: -58.3816 }; // Buenos Aires

const mapContainerStyle = {
  width: '100%',
  height: '200px',
  borderRadius: '10px',
};

/**
 * AddressInput — Google Places autocomplete + map preview.
 * Ported from admin-panel RestaurantFormPage pattern.
 *
 * Props:
 *   value        — current address string
 *   coords       — { lat, lng } or null
 *   onChange      — (address, coords) => void
 *   countryCode  — restrict autocomplete (default 'ar')
 *   disabled     — disables input
 *   error        — error message to display
 */
function AddressInput({
  value = '',
  coords = null,
  onChange,
  countryCode = 'ar',
  disabled = false,
  error,
}) {
  const { t } = useTranslation();
  const autocompleteRef = useRef(null);
  const [mapCenter, setMapCenter] = useState(coords || DEFAULT_CENTER);
  const [markerPosition, setMarkerPosition] = useState(coords);

  const onPlaceChanged = useCallback(() => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();

    if (place.geometry?.location) {
      const location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      const addr = place.formatted_address || place.name || '';
      setMapCenter(location);
      setMarkerPosition(location);
      onChange(addr, location);
    }
  }, [onChange]);

  const onMapClick = useCallback(
    (e) => {
      const location = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      setMarkerPosition(location);
      setMapCenter(location);

      // Reverse geocode
      if (window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location }, (results, status) => {
          if (status === 'OK' && results[0]) {
            onChange(results[0].formatted_address, location);
          } else {
            onChange(value, location);
          }
        });
      } else {
        onChange(value, location);
      }
    },
    [onChange, value]
  );

  // Called when GPS or external source sets coords
  const updateFromCoords = useCallback(
    (newCoords, address) => {
      setMapCenter(newCoords);
      setMarkerPosition(newCoords);
      onChange(address, newCoords);
    },
    [onChange]
  );

  // Expose updateFromCoords via ref if needed
  React.useImperativeHandle(
    React.useRef(null),
    () => ({ updateFromCoords }),
    [updateFromCoords]
  );

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="address-input-wrapper">
        <div className="form-group">
          <label htmlFor="delivery-address">{t('deliveryAddress.addressLabel')}</label>
          <input
            type="text"
            id="delivery-address"
            value={value}
            onChange={(e) => onChange(e.target.value, coords)}
            placeholder={t('deliveryAddress.addressPlaceholder')}
            className={`address-text-input ${error ? 'input-error' : ''}`}
            disabled={disabled}
            autoComplete="off"
          />
        </div>
        {error && <p className="address-error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="address-input-wrapper">
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
        <div className="form-group">
          <label htmlFor="delivery-address">{t('deliveryAddress.addressLabel')}</label>
          <Autocomplete
            onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
            onPlaceChanged={onPlaceChanged}
            options={{
              componentRestrictions: { country: countryCode },
              fields: ['formatted_address', 'geometry', 'name'],
            }}
          >
            <input
              type="text"
              id="delivery-address"
              value={value}
              onChange={(e) => onChange(e.target.value, coords)}
              placeholder={t('deliveryAddress.addressPlaceholder')}
              className={`address-text-input ${error ? 'input-error' : ''}`}
              disabled={disabled}
              autoComplete="off"
            />
          </Autocomplete>
        </div>
        {error && <p className="address-error">{error}</p>}

        <div className="address-map-container">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={markerPosition ? 15 : 12}
            onClick={onMapClick}
            options={{
              gestureHandling: 'greedy',
              scrollwheel: false,
              disableDefaultUI: true,
              zoomControl: true,
            }}
          >
            {markerPosition && <Marker position={markerPosition} />}
          </GoogleMap>
          <small className="map-hint">
            {t('deliveryAddress.mapHint')}
          </small>
        </div>
      </LoadScript>
    </div>
  );
}

export default AddressInput;
